<script lang="ts">
  import type { CohortRow } from '../admin';
  import { navigate } from '../router.svelte';
  import RoleBreakdown from '@ui/RoleBreakdown.svelte';
  import ChartTooltip from '@ui/ChartTooltip.svelte';

  // `linkable` off keeps the hover tooltips live but disables the admin deep-links
  // — the public landing page reuses this chart as a static showcase.
  let { cohorts, linkable = true }: { cohorts: CohortRow[]; linkable?: boolean } = $props();

  // Click a cell → the members table, filtered to that cohort's members who were
  // still contributing at that horizon (the cell's retained numerator).
  function open(c: CohortRow, p: CohortRow['points'][number]): void {
    if (!linkable || p.rate == null || p.retained <= 0) return;
    navigate(`/admin/community/members?cohort=${c.month}&retained=${p.days}`);
  }

  const horizons = $derived(cohorts[0]?.points.map((p) => p.days) ?? []);
  const lastDays = $derived(horizons.at(-1));
  // Day 0 is the intake anchor (everyone who first contributed that period); the
  // rest are retention horizons, the last open-ended ("90d+").
  const hLabel = (d: number): string => (d === 0 ? 'Day 0' : d === lastDays ? `${d}d+` : `${d}d`);

  // Stage-over-stage loss, as a share of whoever was still active at the previous
  // stage. Computed on RATES, never on the raw counts: each horizon carries its own
  // `eligible` (members far enough past their first post to be measurable there), so
  // a recent cohort's count can fall simply because fewer members qualify yet. E.g.
  // a 2026-05-style cohort going 27 → 9 looks like -66.67% by count, but eligible
  // dropped 104 → 71 over the same step; on rates (0.26 → 0.13) the real loss is
  // -51.28%. Null where there's nothing sound to compare against: the day-0 anchor,
  // an unmeasurable stage, or a zero base.
  function deltas(points: CohortRow['points']): (number | null)[] {
    const out: (number | null)[] = [];
    let prev: number | null = null;
    for (const p of points) {
      if (p.rate == null) {
        out.push(null);
        continue;
      }
      out.push(prev != null && prev > 0 ? (p.rate - prev) / prev : null);
      prev = p.rate;
    }
    return out;
  }
  const fmtDelta = (d: number): string => `${d > 0 ? '+' : ''}${(d * 100).toFixed(2)}%`;

  function monthLabel(m: string): string {
    const [y, mo] = m.split('-');
    return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString(undefined, {
      month: 'short',
      year: '2-digit',
    });
  }
  // Retention share → a single-hue primary ramp (deeper = more retained), matching
  // the daily-activity heatmap's intensity language so it blends into the theme.
  // Low reads as a faint primary tint rather than an off-palette red.
  function cellFill(rate: number): number {
    return Math.round(16 + rate * 74); // 16% (low) → 90% (high) primary
  }
  function cellBg(rate: number | null): string {
    return rate == null ? '' : `color-mix(in oklab, var(--color-primary) ${cellFill(rate)}%, transparent)`;
  }
  // Flip the number to primary-content once the fill is dark enough to need it.
  function cellText(rate: number | null): string {
    return rate != null && rate >= 0.5 ? 'text-primary-content' : 'text-base-content';
  }

  // --- hover tooltip (fixed, so it escapes the table's overflow) ---
  type Hover = { c: CohortRow; p: CohortRow['points'][number]; x: number; y: number };
  let hover = $state<Hover | null>(null);
  function enter(e: MouseEvent, c: CohortRow, p: CohortRow['points'][number]): void {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    hover = { c, p, x: r.left + r.width / 2, y: r.top };
  }
</script>

<div class="overflow-x-auto pb-1">
  <!-- min-w-max: the count + delta need ~5rem a column, so let the grid size to its
       content and scroll in the wrapper rather than crushing the cells. -->
  <div
    class="grid w-full min-w-max gap-1 text-center"
    style="grid-template-columns: auto repeat({horizons.length}, minmax(5rem, 1fr))"
  >
    <div></div>
    {#each horizons as d (d)}
      <div class="eyebrow pb-0.5 text-[0.58rem] text-base-content/75">{hLabel(d)}</div>
    {/each}

    {#each cohorts as c (c.month)}
      <div class="flex items-center justify-end gap-1 whitespace-nowrap pr-1.5 font-mono text-[0.65rem] text-base-content/72">
        {monthLabel(c.month)}<span class="text-base-content/54">·{c.size}</span>
      </div>
      {@const ds = deltas(c.points)}
      {#each c.points as p, i (p.days)}
        {@const clickable = linkable && p.rate != null && p.retained > 0}
        {@const d = ds[i]}
        <button
          type="button"
          class="relative flex h-7 items-center justify-center rounded border hair font-mono text-[0.6rem] font-semibold transition-transform hover:z-10 hover:scale-110 {cellText(p.rate)} {p.rate == null ? 'bg-base-100' : ''} {clickable ? 'cursor-pointer' : 'cursor-default'}"
          style:background={cellBg(p.rate)}
          onmouseenter={(e) => enter(e, c, p)}
          onmouseleave={() => (hover = null)}
          onclick={() => open(c, p)}
        >
          {#if p.rate != null}
            {p.retained}
            {#if d != null}
              <!-- opacity, not a fixed colour: the cell flips to primary-content on
                   dark fills, so the delta has to inherit whatever the count uses. -->
              <span class="ml-1 text-[0.55rem] font-normal opacity-70">({fmtDelta(d)})</span>
            {/if}
          {:else}<span class="text-base-content/46">·</span>{/if}
        </button>
      {/each}
    {/each}
  </div>
</div>

<!-- legend: cell = count of members retained; colour = share retained -->
<div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[0.6rem] text-base-content/75">
  <span>number = members still contributing</span>
  <span>(%) = change vs the previous stage</span>
  <span class="flex items-center gap-1.5">
    colour = share
    <span>0%</span>
    <span
      class="h-2 w-24 rounded-full"
      style="background:linear-gradient(to right, color-mix(in oklab, var(--color-primary) 16%, transparent), var(--color-primary))"
    ></span>
    <span>100%</span>
  </span>
</div>

{#if hover}
  {@const h = hover}
  <ChartTooltip x={h.x} y={h.y}>
    <div class="font-mono text-[0.7rem] font-semibold">
      {monthLabel(h.c.month)} cohort · {h.p.days === 0 ? 'first contribution' : `day ${hLabel(h.p.days)}`}
    </div>
    {#if h.p.days === 0}
      <div class="mt-0.5 text-xs text-base-content/70">
        <span class="font-semibold text-primary">{h.p.retained}</span>
        member{h.p.retained === 1 ? '' : 's'} first contributed this period — the cohort's intake.
      </div>
      {#if h.p.retained > 0}
        <div class="mt-2 border-t hair pt-2">
          <RoleBreakdown roles={h.p.roles} total={h.p.retained} label="Roles (all-time)" percent />
        </div>
      {/if}
    {:else if h.p.rate == null}
      <div class="mt-0.5 text-xs text-base-content/72">
        No members are {h.p.days}+ days past their first post yet.
      </div>
    {:else}
      <div class="mt-0.5 text-xs text-base-content/70">
        <span class="font-semibold text-primary">{Math.round(h.p.rate * 100)}%</span> still contributing —
        {h.p.retained} of {h.p.eligible}
        {h.p.eligible < h.c.size ? `(${h.c.size - h.p.eligible} too recent to count)` : ''}
      </div>
      {@const hd = deltas(h.c.points)[h.c.points.indexOf(h.p)]}
      {#if hd != null}
        <div class="mt-1 text-xs text-base-content/70">
          <span class="font-semibold {hd > 0 ? 'text-success' : 'text-error'}">{fmtDelta(hd)}</span>
          vs the previous stage — measured on the retention rate, so it isn't skewed by
          members who aren't old enough to count yet.
        </div>
      {/if}
      {#if h.p.retained > 0}
        <div class="mt-2 border-t hair pt-2">
          <RoleBreakdown roles={h.p.roles} total={h.p.retained} label="Roles of those retained" percent />
        </div>
      {/if}
    {/if}
  </ChartTooltip>
{/if}
